import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  Key,
  Plus,
  Copy,
  Trash2,
  Loader2,
  AlertCircle,
  CheckCircle2,
  ShieldOff,
  ScanLine,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api`;

const AdminApiKeysPage = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [justCreated, setJustCreated] = useState(null);   // plaintext key shown once
  const [scannerKey, setScannerKey] = useState(null);
  const [rotatingScanner, setRotatingScanner] = useState(false);
  const scannerUrl = scannerKey ? `${BASE}/api/scan-iq/scans/${scannerKey}` : "";

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/api-keys`);
      setKeys(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load keys");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadScannerKey = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/scan-iq/scanner-key`);
      setScannerKey(data.api_key);
    } catch {
      /* ignore — card just won't show a URL */
    }
  }, []);

  useEffect(() => { fetchKeys(); loadScannerKey(); }, [fetchKeys, loadScannerKey]);

  const rotateScannerKey = async () => {
    if (!window.confirm("Rotate the scanner key? The current Scanner URL will stop working immediately and every scanner must be updated with the new URL.")) return;
    setRotatingScanner(true);
    try {
      const { data } = await axios.post(`${API}/scan-iq/scanner-key/rotate`);
      setScannerKey(data.api_key);
      toast.success("Scanner key rotated — update your scanners with the new URL");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to rotate key");
    } finally {
      setRotatingScanner(false);
    }
  };

  const createKey = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/admin/api-keys`, { name: newName.trim() });
      setJustCreated(data);
      setNewName("");
      await fetchKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create key");
    } finally {
      setCreating(false);
    }
  };

  const revokeKey = async (k) => {
    if (!window.confirm(`Revoke key "${k.name}"? Any agent using it will stop working immediately.`)) return;
    try {
      await axios.delete(`${API}/admin/api-keys/${k.id}`);
      toast.success("Key revoked");
      await fetchKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to revoke");
    }
  };

  const copyKey = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Key copied to clipboard");
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  const dismissJustCreated = () => setJustCreated(null);

  return (
    <div className="min-h-screen bg-brand-50 flex flex-col">
      <Navbar />
      <main className="flex-grow container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 bg-amber-600 rounded-xl flex items-center justify-center">
            <Key className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-brand-900">API Keys</h1>
            <p className="text-sm text-brand-500">Static tokens for agents & integrations that need to call the Support IQ API without logging in.</p>
          </div>
        </div>

        {/* Scan-IQ Scanner URL — permanent, non-expiring key embedded in the path. Admin-only page. */}
        <div className="bg-white border border-brand-200 rounded-xl p-5 mb-6" data-testid="scanner-url-card">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              <ScanLine className="w-4 h-4 text-teal-600" />
              <h3 className="font-semibold text-brand-900">Scan-IQ Scanner URL</h3>
            </div>
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Never expires</Badge>
          </div>
          <p className="text-xs text-brand-500 mb-3">
            Paste this full URL into the DS2800 scanner's HTTP POST target, with body{" "}
            <code className="bg-brand-100 px-1 rounded">{`{"id":"..","msg":"a|b|c|d"}`}</code>. The key is in the
            URL path, so it works even on scanners that strip query strings or can't send headers.
          </p>
          <div className="flex items-center gap-2 bg-brand-50 border border-brand-200 rounded-md p-2">
            <code className="flex-grow text-xs font-mono text-brand-800 break-all" data-testid="scanner-url-value">
              {scannerUrl || "Loading…"}
            </code>
            <Button size="sm" variant="outline" onClick={() => copyKey(scannerUrl)} disabled={!scannerUrl} data-testid="copy-scanner-url">
              <Copy className="w-4 h-4 mr-1" /> Copy
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={rotateScannerKey}
              disabled={rotatingScanner || !scannerKey}
              className="text-amber-700 border-amber-200 hover:bg-amber-50"
              data-testid="rotate-scanner-key-btn"
            >
              {rotatingScanner ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Rotate
            </Button>
          </div>
        </div>

        {/* One-time reveal banner */}
        {justCreated && (
          <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-4 mb-6" data-testid="new-key-banner">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="flex-grow">
                <h3 className="font-semibold text-emerald-900">Key created — copy it now</h3>
                <p className="text-xs text-emerald-800 mt-1">{justCreated.warning}</p>
                <div className="mt-3 flex items-center gap-2 bg-white border border-emerald-300 rounded-md p-2">
                  <code className="flex-grow text-sm font-mono text-brand-900 break-all" data-testid="new-key-value">
                    {justCreated.key}
                  </code>
                  <Button size="sm" variant="outline" onClick={() => copyKey(justCreated.key)} data-testid="copy-new-key">
                    <Copy className="w-4 h-4 mr-1" /> Copy
                  </Button>
                </div>
              </div>
              <button
                type="button"
                onClick={dismissJustCreated}
                className="text-emerald-700 hover:text-emerald-900 text-sm font-medium"
                data-testid="dismiss-new-key"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Create form */}
        <div className="bg-white border border-brand-200 rounded-xl p-5 mb-6">
          <h3 className="font-semibold text-brand-900 mb-3">Generate a new key</h3>
          <div className="flex gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder='Name this key (e.g. "Zapier agent", "ADL2 reporter")'
              className="flex-grow"
              disabled={creating}
              data-testid="new-key-name"
              onKeyDown={(e) => { if (e.key === "Enter") createKey(); }}
            />
            <Button
              onClick={createKey}
              disabled={creating || !newName.trim()}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="generate-key-btn"
            >
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Generate
            </Button>
          </div>
          <p className="text-xs text-brand-500 mt-2">
            Keys use the format <code className="bg-brand-100 px-1 rounded">siq_…</code>. Pass via
            <code className="bg-brand-100 px-1 rounded ml-1">X-API-Key</code> header or
            <code className="bg-brand-100 px-1 rounded ml-1">?api_key=</code> query param.
            Currently accepted on <code className="bg-brand-100 px-1 rounded">/api/reports/fabrication</code>.
          </p>
        </div>

        {/* List */}
        <div className="bg-white border border-brand-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-brand-200 flex items-center justify-between">
            <h3 className="font-semibold text-brand-900">Active keys</h3>
            <Badge className="bg-brand-100 text-brand-700">{keys.filter(k => !k.revoked).length} active · {keys.filter(k => k.revoked).length} revoked</Badge>
          </div>
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-brand-400" /></div>
          ) : keys.length === 0 ? (
            <div className="p-8 text-center text-brand-400 italic">No keys yet — generate one above.</div>
          ) : (
            <table className="w-full text-sm" data-testid="api-keys-table">
              <thead className="bg-brand-100/50">
                <tr className="text-left text-xs font-semibold text-brand-700">
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Prefix</th>
                  <th className="px-4 py-2">Created</th>
                  <th className="px-4 py-2">Last Used</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {keys.map(k => (
                  <tr key={k.id} className={`border-t border-brand-100 ${k.revoked ? "opacity-50" : "hover:bg-brand-50/40"}`}>
                    <td className="px-4 py-2 font-medium text-brand-900">{k.name}</td>
                    <td className="px-4 py-2 font-mono text-brand-600">{k.key_prefix}</td>
                    <td className="px-4 py-2 text-brand-500 text-xs">{new Date(k.created_at).toLocaleString()}</td>
                    <td className="px-4 py-2 text-brand-500 text-xs">{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "—"}</td>
                    <td className="px-4 py-2">
                      {k.revoked
                        ? <Badge className="bg-red-100 text-red-800 border-red-200"><ShieldOff className="w-3 h-3 mr-1" /> Revoked</Badge>
                        : <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Active</Badge>}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {!k.revoked && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                          onClick={() => revokeKey(k)}
                          data-testid={`revoke-key-${k.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Usage snippet */}
        <div className="bg-brand-100/60 border border-brand-200 rounded-xl p-5 mt-6">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-4 h-4 text-brand-600" />
            <h4 className="font-semibold text-brand-800 text-sm">Sample usage</h4>
          </div>
          <pre className="text-xs bg-white border border-brand-200 rounded-md p-3 overflow-x-auto font-mono text-brand-800">
{`# Header (recommended)
curl -H "X-API-Key: siq_XXXXX…" \\
  "${BASE}/api/reports/fabrication?from=2026-08-01&to=2026-08-31"

# Query param (for URL-only agents)
curl "${BASE}/api/reports/fabrication?from=2026-08-01&to=2026-08-31&api_key=siq_XXXXX…"

# CSV attachment
curl -OJ -H "X-API-Key: siq_XXXXX…" \\
  "${BASE}/api/reports/fabrication?from=2026-08-01&to=2026-08-31&format=csv"`}
          </pre>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminApiKeysPage;
